import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './styles.css';
import { useModalFocusReturn } from '../../hooks/useModalFocusReturn';

const PlanSelectionModal = ({ isOpen, plans, stateName, onPlanSelect }) => {
  const modalRef = useRef(null);

  // Restore focus once this closes. Unlike most other modals this one auto-opens itself (see
  // search.jsx) rather than being triggered by a user click, so there's no specific "trigger
  // button" to return to — but it still needs SOME fix here, because the moment it closes it
  // unmounts (`if (!isOpen) return null;` below) while one of its own plan-option buttons has
  // focus (see the effect right below), which would otherwise drop focus to <body> exactly
  // like every other modal this session. Whatever had focus at open (typically <body> on
  // initial page load, since nothing was clicked to trigger this one) is a harmless, never-worse
  // fallback target.
  useModalFocusReturn(isOpen);

  // Focus management
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const firstButton = modalRef.current?.querySelector('.plan-option-button');
      if (firstButton) {
        firstButton.focus({ preventScroll: true });
      }
    }
  }, [isOpen]);

  // Keyboard navigation (Tab trap)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Tab') {
        const focusableElements = modalRef.current?.querySelectorAll(
          'button:not([disabled])'
        );
        
        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePlanClick = (planCode) => {
    if (onPlanSelect) {
      onPlanSelect(planCode);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="plan-selection-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="plan-selection-modal-title"
    >
      <div className="plan-selection-modal" ref={modalRef}>
        <div className="plan-selection-modal-content">
          <h2 id="plan-selection-modal-title" className="plan-selection-modal-title">
            Choose your plan for {stateName || 'your state'}
          </h2>

          <p className="plan-selection-modal-subheader">
            Each plan has its own network of doctors, specialists and other care providers. Start your search by choosing your plan.
          </p>

          <div className="plan-options-list">
            {plans && plans.length > 0 ? (
              plans.map((plan, index) => (
                <button
                  key={plan.planCode || index}
                  className="plan-option-button"
                  onClick={() => handlePlanClick(plan.planCode)}
                  aria-label={`Select ${plan.planName || plan.planCode}`}
                >
                  <span className="plan-option-text">
                    {plan.planName || plan.planCode}
                  </span>
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 16 16" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    className="plan-option-chevron"
                    aria-hidden="true"
                  >
                    <path 
                      d="M6 12L10 8L6 4" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              ))
            ) : (
              <p className="no-plans-message">No plans available</p>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PlanSelectionModal;
